import { generateClient } from 'aws-amplify/api';
import { type GraphQLResult } from '@aws-amplify/api';
import { post, del } from 'aws-amplify/api';
import { buildShareUrl } from '../utils/shareConversation';

const client = generateClient();

// REST API names (must match the backend contract)
const SHARE_API_NAME = 'shareApi';

// GraphQL queries / mutations (owner-scoped; default auth)
const LIST_SHARED_CONVERSATIONS = `
  query ListMCPSharedConversations($limit: Int, $nextToken: String) {
    listMCPSharedConversations(limit: $limit, nextToken: $nextToken) {
      items {
        id
        conversationId
        messageId
        scope
        title
        conversationType
        isActive
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

// Generated index query field for the sharesByScheduleRunnerId GSI.
const SHARES_BY_RUNNER = `
  query SharesByScheduleRunnerId($scheduleRunnerId: ID!, $limit: Int, $nextToken: String) {
    mCPSharedConversationsByScheduleRunnerIdAndCreatedAt(scheduleRunnerId: $scheduleRunnerId, limit: $limit, nextToken: $nextToken) {
      items {
        id
        isActive
      }
      nextToken
    }
  }
`;

const UPDATE_SHARED_CONVERSATION = `
  mutation UpdateMCPSharedConversation($input: UpdateMCPSharedConversationInput!) {
    updateMCPSharedConversation(input: $input) {
      id
      isActive
      updatedAt
    }
  }
`;

// Types
export interface SharedConversation {
  id: string;
  conversationId: string;
  messageId?: string | null;
  scope: string;
  title: string;
  conversationType?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateShareResult {
  shareId: string;
  url: string;
}

interface CreateShareApiResponse {
  shareId: string;
}

// Service class
export class ShareService {
  // Create a share for a whole conversation, or a single message when messageId is provided.
  static async createShare(
    conversationId: string,
    messageId?: string
  ): Promise<CreateShareResult> {
    try {
      const body: { conversationId: string; messageId?: string } = { conversationId };
      if (messageId) body.messageId = messageId;

      const restOperation = post({
        apiName: SHARE_API_NAME,
        path: '/share',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        options: { body: body as any }, // Amplify's PostInput body requires an `any` cast at the SDK boundary
      });
      const response = await restOperation.response;
      const res = (await response.body.json()) as unknown as CreateShareApiResponse;

      if (!res?.shareId) {
        throw new Error(`Unexpected response shape from createShare: ${JSON.stringify(res)}`);
      }

      return { shareId: res.shareId, url: buildShareUrl(res.shareId) };
    } catch (error) {
      console.error('[ShareService.createShare] Error:', error);
      throw error;
    }
  }

  // Create a LIVE share for a schedule runner's latest output. Tracks the runner (not a fixed
  // conversation), so the snapshot re-generates on each future run — see shareConversationFunction.
  static async createScheduleShare(runnerId: string): Promise<CreateShareResult> {
    try {
      const body = { scope: 'scheduleRunner', scheduleRunnerId: runnerId };

      const restOperation = post({
        apiName: SHARE_API_NAME,
        path: '/share',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        options: { body: body as any }, // Amplify's PostInput body requires an `any` cast at the SDK boundary
      });
      const response = await restOperation.response;
      const res = (await response.body.json()) as unknown as CreateShareApiResponse;

      if (!res?.shareId) {
        throw new Error(`Unexpected response shape from createScheduleShare: ${JSON.stringify(res)}`);
      }

      return { shareId: res.shareId, url: buildShareUrl(res.shareId) };
    } catch (error) {
      console.error('[ShareService.createScheduleShare] Error:', error);
      throw error;
    }
  }

  // Delete (revoke) a share by id.
  static async deleteShare(shareId: string): Promise<void> {
    try {
      const restOperation = del({
        apiName: SHARE_API_NAME,
        path: `/share/${shareId}`,
      });
      await restOperation.response;
    } catch (error) {
      console.error('[ShareService.deleteShare] Error:', error);
      throw error;
    }
  }

  // List the current user's shares (owner-scoped via default auth).
  static async listShares(): Promise<SharedConversation[]> {
    try {
      const result = (await client.graphql({
        query: LIST_SHARED_CONVERSATIONS,
        variables: { limit: 100 },
      })) as GraphQLResult<{
        listMCPSharedConversations: { items: SharedConversation[]; nextToken?: string | null };
      }>;

      return result.data?.listMCPSharedConversations?.items ?? [];
    } catch (error) {
      console.error('[ShareService.listShares] Error:', error);
      return [];
    }
  }

  // List ACTIVE live-share ids for a runner, following pagination. Errors resolve to [] —
  // callers use this for cascade-deactivation and UI counts, neither of which should throw.
  static async listActiveShareIdsForRunner(runnerId: string): Promise<string[]> {
    const ids: string[] = [];
    let nextToken: string | null | undefined;
    try {
      do {
        const result = (await client.graphql({
          query: SHARES_BY_RUNNER,
          variables: { scheduleRunnerId: runnerId, limit: 100, nextToken },
        })) as GraphQLResult<{
          mCPSharedConversationsByScheduleRunnerIdAndCreatedAt: {
            items: { id: string; isActive: boolean }[];
            nextToken?: string | null;
          };
        }>;
        const page = result.data?.mCPSharedConversationsByScheduleRunnerIdAndCreatedAt;
        ids.push(...(page?.items ?? []).filter((s) => s.isActive).map((s) => s.id));
        nextToken = page?.nextToken;
      } while (nextToken);
    } catch (error) {
      console.error('[ShareService.listActiveShareIdsForRunner] Error:', error);
    }
    return ids;
  }

  static async listActiveShareIdsForRunners(runnerIds: string[]): Promise<string[]> {
    const perRunner = await Promise.all(
      runnerIds.map((id) => ShareService.listActiveShareIdsForRunner(id))
    );
    return perRunner.flat();
  }

  // Best-effort bulk deactivate (cascade when a schedule/runner is deleted). Shares owned
  // by another user fail the owner-auth update — counted, never thrown, so the delete
  // they cascade from still goes through.
  static async deactivateShares(shareIds: string[]): Promise<{ deactivated: number; failed: number }> {
    let deactivated = 0;
    let failed = 0;
    await Promise.all(
      shareIds.map(async (id) => {
        try {
          await ShareService.setShareActive(id, false);
          deactivated++;
        } catch {
          failed++;
        }
      })
    );
    if (failed > 0) {
      console.warn(`[ShareService.deactivateShares] ${failed}/${shareIds.length} share(s) could not be deactivated`);
    }
    return { deactivated, failed };
  }

  // Toggle a share active/inactive.
  static async setShareActive(shareId: string, isActive: boolean): Promise<void> {
    try {
      await client.graphql({
        query: UPDATE_SHARED_CONVERSATION,
        variables: { input: { id: shareId, isActive } },
      });
    } catch (error) {
      console.error('[ShareService.setShareActive] Error:', error);
      throw error;
    }
  }

  // Toggle whether a static agent-conversation share shows the owner's prompts.
  static async setShareIncludePrompts(shareId: string, includePrompts: boolean): Promise<void> {
    try {
      await client.graphql({
        query: UPDATE_SHARED_CONVERSATION,
        variables: { input: { id: shareId, includePrompts } },
      });
    } catch (error) {
      console.error('[ShareService.setShareIncludePrompts] Error:', error);
      throw error;
    }
  }
}

export default ShareService;
